/**
 * トグル機能
 * トグルアイテムにjs-toggle-itemを付与
 * トグルトリガーにjs-toggle-item-triggerを付与
 * トグルコンテンツにjs-toggle-item-contentを付与
 * トグルトリガーをクリックすると、対応するコンテンツが表示/非表示される
 */

/**
 * アニメーションの時間とイージング
 */
const animTiming = {
  duration: 400,
  easing: "ease-out"
};

/**
 * トグルを閉じるときのキーフレームを作成します。
 * @param content {HTMLElement}
 */
const closingAnimKeyframes = (content) => [
  {
    height: content.offsetHeight + 'px',
    opacity: 1,
  }, {
    height: 0,
    opacity: 0,
  }
];

/**
 * トグルを開くときのキーフレームを作成します。
 * @param content {HTMLElement}
 * @param targetHeight {number} アニメーションの目標高さ（px）
 */
const openingAnimKeyframes = (content, targetHeight) => {
  return [
    {
      height: 0,
      opacity: 0,
    }, {
      height: targetHeight + 'px',
      opacity: 1,
    }
  ];
};

const initToggle = () => {
  const toggleItems = document.querySelectorAll('.js-toggle-item');
  
  if (toggleItems.length === 0) return;
  
  const RUNNING_VALUE = "running"; // アニメーション実行中のときに付与する予定のカスタムデータ属性の値
  const IS_OPENED_CLASS = "is-opened"; // 開閉状態操作用のクラス名
  
  // 要素を一度だけ取得して配列に保存（querySelectorの重複実行を防ぐ）
  const toggleData = [];
  toggleItems.forEach(toggleItem => {
    const trigger = toggleItem.querySelector('.js-toggle-item-trigger');
    const content = toggleItem.querySelector('.js-toggle-item-content');
    
    if (trigger && content) {
      toggleData.push({ toggleItem, trigger, content });
    }
  });
  
  if (toggleData.length === 0) return;
  
  // PC時（768px以上）の初期化：子メニューを常に表示
  const initPC = () => {
    toggleData.forEach(({ toggleItem, trigger, content }) => {
      // PC時は子メニューを常に表示
      content.style.display = 'block';
      content.style.opacity = '1';
      content.style.height = 'auto';
      trigger.setAttribute('aria-expanded', 'true');
      toggleItem.classList.add(IS_OPENED_CLASS);
    });
  };
  
  // モバイル時（767px以下）の初期化：子メニューを閉じた状態にする
  const initMobile = () => {
    toggleData.forEach(({ toggleItem, trigger, content }) => {
      // モバイル時は初期状態に戻す（閉じた状態）
      content.style.display = 'none';
      content.style.opacity = '0';
      content.style.height = '0px';
      trigger.setAttribute('aria-expanded', 'false');
      toggleItem.classList.remove(IS_OPENED_CLASS);
      // アニメーション状態をリセット
      toggleItem.dataset.animStatus = "";
    });
  };
  
  // ブレイクポイントを超えたときの処理
  const mediaQuery = window.matchMedia('(min-width: 1024px)');
  const handleMediaChange = (e) => {
    if (e.matches) {
      // PC時（768px以上）：子メニューを常に表示
      initPC();
    } else {
      // モバイル時（767px以下）：子メニューを閉じた状態にする
      initMobile();
    }
  };
  
  // 初期状態のチェック
  if (mediaQuery.matches) {
    initPC();
  } else {
    initMobile();
  }
  
  // メディアクエリの変更を監視
  mediaQuery.addEventListener('change', handleMediaChange);
  
  // クリックイベントの設定
  toggleData.forEach(({ toggleItem, trigger, content }) => {
    trigger.addEventListener('click', () => {
      // PC時（768px以上）はトグル機能を無効化
      if (mediaQuery.matches) {
        return;
      }
      
      // 連打防止用。アニメーション中だったらクリックイベントを受け付けないでリターンする
      if (toggleItem.dataset.animStatus === RUNNING_VALUE) {
        return;
      }
      
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // 閉じる
        trigger.setAttribute('aria-expanded', 'false');
        toggleItem.classList.remove(IS_OPENED_CLASS);
        
        // アニメーションを実行
        const closingAnim = content.animate(closingAnimKeyframes(content), animTiming);
        // アニメーション実行中用の値を付与
        toggleItem.dataset.animStatus = RUNNING_VALUE;
        
        // アニメーションの完了後に
        closingAnim.onfinish = () => {
          content.style.display = 'none';
          content.style.opacity = '0';
          content.style.height = '0px';
          // アニメーション実行中用の値を取り除く
          toggleItem.dataset.animStatus = "";
        };
      } else {
        // 開く
        trigger.setAttribute('aria-expanded', 'true');
        toggleItem.classList.add(IS_OPENED_CLASS);
        
        // アニメーション前にdisplay: blockを設定して高さを取得できるようにする
        content.style.display = 'block';
        // heightを一時的に削除して、実際の高さを取得
        content.style.height = '';
        content.style.opacity = '0';
        
        // レイアウトを再計算してから、実際の高さを取得
        // scrollHeightを使うことで、height: 0pxが設定されていても正確な高さを取得できる
        void content.offsetHeight;
        const targetHeight = content.scrollHeight;
        
        // アニメーション開始状態を設定（高さを0に）
        content.style.height = '0px';
                
        // アニメーションを実行
        const openingAnim = content.animate(openingAnimKeyframes(content, targetHeight), animTiming);
        // アニメーション実行中用の値を入れる
        toggleItem.dataset.animStatus = RUNNING_VALUE;
        
        // アニメーション完了後にアニメーション実行中用の値を取り除く
        openingAnim.onfinish = () => {
          // アニメーション完了後、インラインスタイルをクリア（CSSに任せる）
          content.style.height = 'auto';
          content.style.opacity = '1';
          toggleItem.dataset.animStatus = "";
        };
      }
    });
  });
};

// type="module"のスクリプトはDOMContentLoadedの後に実行されるため、単純に呼び出すだけで良い
initToggle();

